import { BillingCycle, SubscriptionPackage } from '@/renderer/types/billing';
import { getEnvVar } from '@/renderer/util';

type StripePackageId = SubscriptionPackage;
type StripePriceField = 'stripe_price_id' | 'annual_stripe_price_id';

const stripePriceEnvKeys: Record<
  StripePackageId,
  Record<BillingCycle, { development: string; production: string }>
> = {
  enterprise: {
    monthly: {
      development: 'STRIPE_ENTERPRISE_PRICE_ID',
      production: 'STRIPE_ENTERPRISE_PRICE_ID_PROD',
    },
    semi_anually: {
      development: '',
      production: '',
    },
    annual: {
      development: 'STRIPE_ENTERPRISE_ANNUAL_PRICE_ID',
      production: 'STRIPE_ENTERPRISE_ANNUAL_PRICE_ID_PROD',
    },
  },
  professional: {
    monthly: {
      development: 'STRIPE_PROFESSIONAL_PRICE_ID',
      production: 'STRIPE_PROFESSIONAL_PRICE_ID_PROD',
    },
    semi_anually: {
      development: '',
      production: '',
    },
    annual: {
      development: 'STRIPE_PROFESSIONAL_ANNUAL_PRICE_ID',
      production: 'STRIPE_PROFESSIONAL_ANNUAL_PRICE_ID_PROD',
    },
  },
  starter: {
    monthly: {
      development: 'STRIPE_STARTER_PRICE_ID',
      production: 'STRIPE_STARTER_PRICE_ID_PROD',
    },
    semi_anually: {
      development: '',
      production: '',
    },
    annual: {
      development: 'STRIPE_STARTER_ANNUAL_PRICE_ID',
      production: 'STRIPE_STARTER_ANNUAL_PRICE_ID_PROD',
    },
  },
  pro_semi_annual: {
    monthly: {
      development: '',
      production: '',
    },
    semi_anually: {
      development: 'STRIPE_PRO_SEMI_ANNUAL_PRICE_ID',
      production: 'STRIPE_PRO_SEMI_ANNUAL_PRICE_ID_PROD',
    },
    annual: {
      development: 'STRIPE_PRO_SEMI_ANNUAL_ANNUAL_PRICE_ID',
      production: 'STRIPE_PRO_SEMI_ANNUAL_ANNUAL_PRICE_ID_PROD',
    },
  },
};

export const isProductionBillingMode = () => {
  const nodeEnv = getEnvVar('NODE_ENV');
  const appEnv = getEnvVar('APP_ENV');

  return nodeEnv === 'production' || appEnv === 'production';
};

export const getStripePriceId = (
  packageId: StripePackageId,
  billingCycle?: BillingCycle,
  fallback?: string | null,
) => {
  const resolvedBillingCycle = billingCycle || 'monthly';
  const envKeys = stripePriceEnvKeys[packageId]?.[resolvedBillingCycle];

  if (!envKeys) {
    return fallback || '';
  }

  const productionValue = getEnvVar(envKeys.production);
  const developmentValue = getEnvVar(envKeys.development);

  if (isProductionBillingMode()) {
    return productionValue || developmentValue || fallback || '';
  }

  return developmentValue || productionValue || fallback || '';
};

export const resolveStripePriceId = <
  T extends {
    id: StripePackageId;
    stripe_price_id: string;
    annual_stripe_price_id?: string | null;
  },
>(
  packageInfo: T,
) => {
  const resolvedPriceIds: Record<StripePriceField, string | null> = {
    stripe_price_id:
      getStripePriceId(
        packageInfo.id,
        'monthly',
        packageInfo.stripe_price_id,
      ) || packageInfo.stripe_price_id,
    annual_stripe_price_id:
      getStripePriceId(
        packageInfo.id,
        'annual',
        packageInfo.annual_stripe_price_id,
      ) ||
      packageInfo.annual_stripe_price_id ||
      null,
  };

  return {
    ...packageInfo,
    ...resolvedPriceIds,
  };
};
